import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logoWhite from "../../assets/images/logo-white.svg";
import "../../assets/styles/bookdemo.css";

const getApiBaseUrl = () => {
  const configured =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    "https://backend.imergey.com/api";
  return configured.replace(/\/+$/, "");
};

const AGENT = {
  name: "Julie",
  role: "Demo Concierge · IMERGEY",
};

const INTEREST_OPTIONS = [
  "Content & scheduling",
  "Brand management",
  "Analytics & reporting",
  "Show me everything",
];

const firstNameOf = (full) => (full || "").trim().split(/\s+/)[0] || "there";

const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

// Next `count` weekdays starting tomorrow, as { label, sub, value:YYYY-MM-DD }
const upcomingWeekdays = (count = 8) => {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
      days.push({
        label: d.toLocaleDateString("en-GB", { weekday: "short" }),
        sub: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
        value,
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const formatSlotLabel = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const formatFull = (iso) =>
  new Date(iso).toLocaleString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "numeric",
    minute: "2-digit",
  });

let msgId = 0;

export default function BookDemo() {
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState("boot"); // boot → name → email → company → interest → date → slot → confirm → booking → done
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [slots, setSlots] = useState(null);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const days = useMemo(() => upcomingWeekdays(8), []);
  const streamRef = useRef(null);
  const inputRef = useRef(null);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      const el = streamRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  const pushUser = (text) => {
    setMessages((m) => [...m, { id: ++msgId, from: "user", text }]);
    scrollDown();
  };

  // Push agent messages one by one with a typing indicator between them,
  // then move to the next step.
  const agentSay = (texts, nextStep, extra = {}) => {
    const list = Array.isArray(texts) ? texts : [texts];
    let delay = 350;
    list.forEach((text, i) => {
      setTimeout(() => {
        setTyping(true);
        scrollDown();
      }, delay);
      const typeTime = Math.min(600 + text.length * 14, 1700);
      delay += typeTime;
      const isLast = i === list.length - 1;
      setTimeout(() => {
        setTyping(false);
        setMessages((m) => [...m, { id: ++msgId, from: "agent", text, ...(isLast ? extra : {}) }]);
        if (isLast && nextStep) setStep(nextStep);
        scrollDown();
      }, delay);
      delay += 250;
    });
  };

  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return; // StrictMode double-mount guard
    bootedRef.current = true;
    agentSay(
      [
        "Hey! 👋 I'm Julie, part of the IMERGEY team.",
        "I'll get you a personal demo with one of our specialists — takes under a minute, promise.",
        "First things first — what's your name?",
      ],
      "name"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (["name", "email", "company"].includes(step)) inputRef.current?.focus();
  }, [step]);

  const submitText = () => {
    const value = inputValue.trim();
    if (step === "name") {
      if (!value) return setInputError("A name would help 🙂");
      setInputError("");
      setInputValue("");
      pushUser(value);
      setAnswers((a) => ({ ...a, name: value }));
      agentSay(
        [
          `Lovely to meet you, ${firstNameOf(value)}! 🙌`,
          "What's your email? That's where your calendar invite will land.",
        ],
        "email"
      );
    } else if (step === "email") {
      if (!isValidEmail(value)) return setInputError("Hmm, that email doesn't look right — mind checking it?");
      setInputError("");
      setInputValue("");
      pushUser(value);
      setAnswers((a) => ({ ...a, email: value }));
      agentSay(["Perfect ✅", "And which company are you with? (Totally fine to skip — just hit send.)"], "company");
    } else if (step === "company") {
      setInputError("");
      setInputValue("");
      if (value) pushUser(value);
      else pushUser("(skipped)");
      setAnswers((a) => ({ ...a, company: value }));
      agentSay(
        ["Got it. So I can point the right specialist your way — what should we focus on?"],
        "interest",
        { chips: INTEREST_OPTIONS, chipKey: "interest" }
      );
    }
  };

  const pickInterest = (opt) => {
    pushUser(opt);
    setAnswers((a) => ({ ...a, interest: opt }));
    setStep("waiting");
    agentSay(
      ["Great choice 👌", "Now the fun part — pick a day that works for you:"],
      "date",
      { days: true }
    );
  };

  const pickDate = async (day) => {
    pushUser(`${day.label}, ${day.sub}`);
    setAnswers((a) => ({ ...a, date: day.value }));
    setStep("waiting");
    setTyping(true);
    scrollDown();
    try {
      const res = await fetch(`${getApiBaseUrl()}/user/demo-booking/slots?date=${day.value}`);
      const json = await res.json();
      const available = (json?.data?.slots || []).filter((s) => s.available);
      setTyping(false);
      if (!available.length) {
        agentSay(
          ["Ah, that day is fully booked out 😅 Popular one! Could you try another day?"],
          "date",
          { days: true }
        );
        return;
      }
      setSlots(available);
      const visitorTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      agentSay(
        [`Checking the calendar… here's what's free that day 👇 (times shown in your timezone — ${visitorTz})`],
        "slot",
        { slots: true }
      );
    } catch (e) {
      setTyping(false);
      agentSay(
        ["I'm having trouble reaching our calendar right now 😔 Give it another go?"],
        "date",
        { days: true }
      );
    }
  };

  const pickSlot = (slot) => {
    pushUser(formatSlotLabel(slot.start));
    setAnswers((a) => ({ ...a, slot }));
    setStep("waiting");
    agentSay(["Almost there — does this look right?"], "confirm", { confirm: true });
  };

  const confirmBooking = async () => {
    const a = answersRef.current;
    pushUser("Yes, book it! 🎉");
    setStep("booking");
    setTyping(true);
    scrollDown();
    try {
      const res = await fetch(`${getApiBaseUrl()}/user/demo-booking/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: a.name,
          email: a.email,
          company: a.company,
          interest: a.interest,
          slotStart: a.slot.start,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const json = await res.json();
      setTyping(false);
      if (!res.ok) {
        if (res.status === 409) {
          agentSay(
            ["Oh no — someone grabbed that slot seconds before you 😱 Let's pick another day:"],
            "date",
            { days: true }
          );
          return;
        }
        throw new Error(json?.message || "Booking failed");
      }
      const meetingLink = json?.data?.meetingLink;
      setAnswers((prev) => ({ ...prev, meetingLink }));
      agentSay(
        [
          `Done! 🎉 You're booked for ${formatFull(a.slot.start)}.`,
          "A calendar invite is on its way to your inbox — open it in Outlook and hit Accept, and it's locked in.",
          meetingLink
            ? "Here's your Teams link too, so you've got it either way 👇"
            : "Our specialist can't wait to meet you. See you there! 💙",
        ],
        "done",
        { celebrate: true, meetingLink }
      );
    } catch (e) {
      setTyping(false);
      agentSay(["Something went wrong on my end 😔 " + (e.message || "") + " — shall we try the time again?"], "confirm", {
        confirm: true,
      });
    }
  };

  const editBooking = () => {
    pushUser("Let me change the time");
    setStep("waiting");
    agentSay(["No problem at all — pick another day:"], "date", { days: true });
  };

  const showInput = ["name", "email", "company"].includes(step);
  const a = answers;

  return (
    <div className="bd-page">
      <div className="bd-glow bd-glow-a" />
      <div className="bd-glow bd-glow-b" />

      <header className="bd-header">
        <Link to="/">
          <img src={logoWhite} alt="IMERGEY" className="bd-logo" />
        </Link>
        <Link to="/" className="bd-back">← Back to site</Link>
      </header>

      <div className="bd-layout">
        <aside className="bd-pitch">
          <h1>
            See IMERGEY <span>in action.</span>
          </h1>
          <p>
            30 minutes, zero slides you don't care about. A specialist walks you through the
            exact workflows you'll use every day.
          </p>
          <ul>
            <li>✦ Tailored to your focus area</li>
            <li>✦ Calendar invite straight to your inbox</li>
            <li>✦ No commitment, no pressure</li>
          </ul>
        </aside>

        <main className="bd-chat" aria-live="polite">
          <div className="bd-chat-top">
            <div className="bd-avatar">
              {AGENT.name[0]}<span className="bd-online" />
            </div>
            <div>
              <div className="bd-agent-name">{AGENT.name}</div>
              <div className="bd-agent-role">{AGENT.role} · replies instantly</div>
            </div>
          </div>

          <div className="bd-stream" ref={streamRef}>
            {messages.map((m) => (
              <div key={m.id} className={`bd-row bd-row-${m.from}`}>
                {m.from === "agent" && <div className="bd-avatar bd-avatar-sm">{AGENT.name[0]}</div>}
                <div className="bd-bubble-wrap">
                  <div className={`bd-bubble bd-bubble-${m.from} ${m.celebrate ? "bd-celebrate" : ""}`}>
                    {m.text}
                  </div>

                  {m.meetingLink && step === "done" && (
                    <a className="bd-teams-link" href={m.meetingLink} target="_blank" rel="noreferrer">
                      🔗 Join Microsoft Teams Meeting
                    </a>
                  )}

                  {m.chips && step === "interest" && (
                    <div className="bd-chips">
                      {m.chips.map((c) => (
                        <button key={c} className="bd-chip" onClick={() => pickInterest(c)}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}

                  {m.days && step === "date" && (
                    <div className="bd-days">
                      {days.map((d) => (
                        <button key={d.value} className="bd-day" onClick={() => pickDate(d)}>
                          <span>{d.label}</span>
                          <b>{d.sub}</b>
                        </button>
                      ))}
                    </div>
                  )}

                  {m.slots && step === "slot" && slots && (
                    <div className="bd-slots">
                      {slots.map((s) => (
                        <button key={s.start} className="bd-slot" onClick={() => pickSlot(s)}>
                          {formatSlotLabel(s.start)}
                        </button>
                      ))}
                    </div>
                  )}

                  {m.confirm && step === "confirm" && a.slot && (
                    <div className="bd-card">
                      <div className="bd-card-when">
                        {formatFull(a.slot.start)}
                        <span className="bd-card-tz"> · {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
                      </div>
                      <div className="bd-card-line">
                        {a.name}
                        {a.company ? ` · ${a.company}` : ""}
                      </div>
                      <div className="bd-card-line">{a.email}</div>
                      <div className="bd-card-line bd-card-focus">{a.interest}</div>
                      <div className="bd-card-actions">
                        <button className="bd-confirm" onClick={confirmBooking}>
                          Book it 🎉
                        </button>
                        <button className="bd-edit" onClick={editBooking}>
                          Change time
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {typing && (
              <div className="bd-row bd-row-agent">
                <div className="bd-avatar bd-avatar-sm">{AGENT.name[0]}</div>
                <div className="bd-bubble bd-bubble-agent bd-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            {step === "done" && (
              <div className="bd-confetti" aria-hidden="true">
                {Array.from({ length: 24 }).map((_, i) => (
                  <i key={i} style={{ "--i": i }} />
                ))}
              </div>
            )}
          </div>

          <div className={`bd-inputbar ${showInput ? "" : "bd-inputbar-off"}`}>
            {inputError && <div className="bd-error">{inputError}</div>}
            <div className="bd-inputrow">
              <input
                ref={inputRef}
                value={inputValue}
                disabled={!showInput}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitText()}
                placeholder={
                  step === "name"
                    ? "Type your name…"
                    : step === "email"
                    ? "you@company.com"
                    : step === "company"
                    ? "Company name (optional)"
                    : "Pick an option above 👆"
                }
              />
              <button onClick={submitText} disabled={!showInput} aria-label="Send">
                ➤
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
